import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
// Import Tabs and TabItem from flowbite-react
import { Alert, Spinner, Button, Label, Pagination, Tabs, TabItem } from 'flowbite-react';
import api from '../../../services/api/axiosConfig';
import {
    FaUserCircle,
    FaEnvelope,
    FaMapMarkerAlt,
    FaPhone,
    FaBuilding,
    FaInfoCircle
} from 'react-icons/fa';

import Breadcrumb from '../../dashboard/pages/components/BreadCrumb';
import ConfirmationModal from '../../../components/ConfirmationModal';
import CustomerDocumentListing from '../components/CustomerDocumentListing';
import { FaFileAlt, FaPlus } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css'; // Make sure to import the CSS!
import UploadCustomerDocument from '../components/UploadCustomerDocument';
import API_ENDPOINTS,{buildEndpoint} from '../../../services/api/endpoints';
import { toast } from 'react-toastify';
import CustomerEmailLogs from '../../customer_mail/pages/components/CustomerEmailLogs';
import useFindOrCreateConversation from '../../../hooks/useFindOrCreateConversation';

const ShowCustomerPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [customerDetails, setCustomerDetails] = useState(null);
    const [loadingCustomer, setLoadingCustomer] = useState(true);
    const [error, setError] = useState(null);

    const [customerDocuments, setCustomerDocuments] = useState([]);
    const [loadingDocuments, setLoadingDocuments] = useState(true);
    const [documentError, setDocumentError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);


    // State for CUSTOMER delete confirmation modal
    const [showDeleteCustomerModal, setShowDeleteCustomerModal] = useState(false);
    const [customerToDelete, setCustomerToDelete] = useState(null);
    const [deletingCustomer, setDeletingCustomer] = useState(false);

    // NEW State for DOCUMENT delete confirmation modal
    const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
    const [documentToDeleteId, setDocumentToDeleteId] = useState(null);
    const [documentToDeleteName, setDocumentToDeleteName] = useState('');
    const [deletingDocument, setDeletingDocument] = useState(false);

    // State for document filtering by date
    const [selectedDate, setSelectedDate] = useState(null); // Will store a Date object

    // State for active tab
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'documents'


    const [pagination, setPagination] = useState({
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1,
        from: 0,
        to: 0,
    });

    const onPageChange = (page) => {
        setPagination(prev => ({ ...prev, current_page: page }));
        fetchCustomerDocuments(page);
    };

    const handleDocumentStatusChange = (docId, newStatus) => {
        setCustomerDocuments(prevDocs => 
            prevDocs.map(doc => 
            doc.id === docId ? {...doc, verified_status: newStatus} : doc
            )
        );
    };

    // --- Define API fetching functions outside useEffect, wrapped in useCallback ---

    const fetchCustomerDetails = useCallback(async (page = 1) => {


        setLoadingCustomer(true);
        setError(null);
        try {
            const response = await api.get(buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.SHOW,{id}));

            if (response.data.statuscode==201) {
                setCustomerDetails(response.data.body);
                document.title = `CPA - Customer ${response.data.body.FirstName} ${response.data.body.LastName}`;

            }else{
                setError( 'Customer not found or failed to fetch details.');
            }

        } catch (err) {
            console.error('Error fetching customer details:', err);
            if (err.response) {
                setError(err.response.data.message || `Failed to load customer. Status: ${err.response.status}`);
            } else if (err.request) {
                setError('Network Error: No response from server when fetching customer details.');
            } else {
                setError('An unexpected error occurred while fetching customer details.');
            }
        } finally {
            setLoadingCustomer(false);
        }
    }, [id]); // Dependency: id

   // Or handle cases where address might be incomplete


    const fetchCustomerDocuments = useCallback(async () => {
        setLoadingDocuments(true);
        setDocumentError(null);
        try {
            const params = {
                customer_id: id,
            };

            // Add date filter parameter if a date is selected
            if (selectedDate) {
                // Format the date to 'YYYY-MM-DD' for the API
                params.date_filter = selectedDate.toISOString().split('T')[0];
                console.log('Fetching documents with date filter:', params.date_filter);
            } else {
                console.log('Fetching documents without date filter.');
            }

            // Adjust this API endpoint if your backend uses a different one for filtered documents
            const response = await api.get(buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.DOCUMENT.LISTING,  { customerId: id }), { params });

            if (response.data.success && response.data.data) {
                const fetchedData = response.data.data;
                const fetchedPagination = response.data.pagination;

                if (Array.isArray(fetchedData) &&
                    fetchedPagination &&
                    typeof fetchedPagination.total === 'number' &&
                    typeof fetchedPagination.per_page === 'number' &&
                    typeof fetchedPagination.current_page === 'number' &&
                    typeof fetchedPagination.last_page === 'number' &&
                    typeof fetchedPagination.from === 'number' &&
                    typeof fetchedPagination.to === 'number'
                ) {
                    setCustomerDocuments(response.data.data);
                    setPagination(fetchedPagination);
                } else {
                    setError('Received invalid data structure from the API.');
                }
            } else {
                setDocumentError(response.data.message || 'Failed to fetch customer documents.');
                setCustomerDocuments([]);
            }
        } catch (err) {
            console.error('Error fetching customer documents:', err);
            if (err.response) {
                setDocumentError(err.response.data.message || `Failed to load documents. Status: ${err.response.status}`);
            } else if (err.request) {
                setDocumentError('Network Error: No response from server when fetching documents.');
            } else {
                setDocumentError('An unexpected error occurred while fetching documents.');
            }
        } finally {
            setLoadingDocuments(false);
        }
    }, [id, selectedDate]); // Dependencies: id and selectedDate

    // --- useEffect for initial data fetch ---
    useEffect(() => {
        document.title = customerDetails?.full_name
            ? `CPA - Customer ${customerDetails.full_name}`
            : 'CPA - Loading Customer...';
        if (id) {
            fetchCustomerDetails();
        } else {
            setLoadingCustomer(false);
            setLoadingDocuments(false);
            setError('Customer ID is missing from the URL.');
        }

    }, [id, customerDetails?.full_name, fetchCustomerDetails]);


    // This useEffect specifically for documents, triggered by ID or selectedDate
    useEffect(() => {
        // Only fetch documents if the documents tab is active
        if (id && activeTab === 'documents') {
            fetchCustomerDocuments();
        }
    }, [id, selectedDate, activeTab, fetchCustomerDocuments]); // Add activeTab to dependencies


    const { initiateChat, isLoadingChat, chatError } = useFindOrCreateConversation();


    // --- Customer Delete Functionality ---
    const handleDeleteCustomerClick = (customer) => {
        setCustomerToDelete(customer);
        setShowDeleteCustomerModal(true);
    };

    const confirmCustomerDelete = async () => {
        if (!customerToDelete) return;

        setDeletingCustomer(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const response = await api.delete(buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.DELETE,  { id: customerToDelete.id }));

            if (response.data.success) {
                toast.success(response.data.message || 'Customer deleted successfully!');
                navigate('/dashboard/customer-list');
            } else {
                setError(response.data.message || 'Failed to delete customer.');
            }
        } catch (err) {
            console.error('Error deleting customer:', err);
            if (err.response) {
                setError(err.response.data.message || `Error: ${err.response.status} - ${err.response.statusText}`);
            } else if (err.request) {
                setError('Network Error: No response from server during deletion.');
            } else {
                setError('An unexpected error occurred during deletion.');
            }
        } finally {
            setDeletingCustomer(false);
            setShowDeleteCustomerModal(false);
            setCustomerToDelete(null);
        }
    };

    const closeDeleteCustomerModal = () => {
        setShowDeleteCustomerModal(false);
        setCustomerToDelete(null);
        setDeletingCustomer(false);
    };

    const handleDeleteDocumentClick = (docId, docName) => {
        setDocumentToDeleteId(docId);
        setDocumentToDeleteName(docName);
        setShowDeleteDocumentModal(true);
    };

    const confirmDocumentDelete = async () => {
        if (!documentToDeleteId) return;

        setDeletingDocument(true);
        setDocumentError(null);

        try {
            const response = await api.delete(buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.DOCUMENT.DELETE,  { id: documentToDeleteId }));
            if (response.data.success) {
                toast.success('Document deleted successfully!');
                fetchCustomerDocuments(); // Re-fetch documents to update the list
            } else {
                setDocumentError(response.data.message || 'Failed to delete document.');
            }
        } catch (err) {
            console.error('Error deleting document:', err);
            if (err.message) {
                setDocumentError(err.message);
            } else if (err.request) {
                setDocumentError('Network Error: No response from server during document deletion.');
            } else {
                setDocumentError('An unexpected error occurred during document deletion.');
            }
        } finally {
            setDeletingDocument(false);
            setShowDeleteDocumentModal(false);
            setDocumentToDeleteId(null);
            setDocumentToDeleteName('');
        }
    };

    const closeDeleteDocumentModal = () => {
        setShowDeleteDocumentModal(false);
        setDocumentToDeleteId(null);
        setDocumentToDeleteName('');
        setDeletingDocument(false);
    };


    const breadcrumbItems = [
        { label: 'Dashboard', url: '/dashboard' },
        { label: 'Customers', url: '/dashboard/customer-list' },
        { label: 'Customer Profile', url: '#', current: true }
    ];

    const profile = customerDetails ? {
        name: customerDetails.FirstName+" "+customerDetails.LastName,
        title: customerDetails.BusinessName ? `Business : ${customerDetails.BusinessName}` : 'Customer Profile',
        contactInfo: {
            email: customerDetails.Email,
            phone: customerDetails.Phone,
            street_address: customerDetails.StreetAddress,
            city : customerDetails.City,
            state : customerDetails.Address,
            zip_code : customerDetails.ZipCode,
            createdAt: customerDetails.AddedDate,
            updatedAt: customerDetails.updated_at
        },
        notes:  'Bio feature is coming soon.'
    } : null;

     const formattedAddress = profile?.contactInfo.street_address ?
    `${profile.contactInfo.street_address}, ${profile.contactInfo.city}, ${profile.contactInfo.state} ${profile.contactInfo.zip_code}` :
    'N/A'; 

    if (loadingCustomer) {
        return (
            <div className="min-h-screen bg-gray-100 p-6 flex justify-center items-center">
                <Spinner size="xl" /> <span className="ml-3 text-lg ">Loading Customer Details...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-100 p-6 flex justify-center items-center flex-col">
                <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
                    <span className="font-medium">Error:</span> {error}
                </Alert>
                <Button onClick={() => navigate('/dashboard/customer-list')} className="bg-blue-500 hover:bg-blue-600">
                    Go to Customer List
                </Button>
            </div>
        );
    }

    if (!customerDetails || !profile) {
        return (
            <div className="min-h-screen bg-gray-100 p-6 text-center flex justify-center items-center flex-col">
                <h2 className="text-2xl font-bold mb-4">Customer Not Found</h2>
                <p className="mb-6">The customer you are looking for does not exist or was deleted.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-6">
            <Breadcrumb items={breadcrumbItems} />

            {/* Main Header Section */}
            <div className="relative flex flex-col md:flex-row md:items-start gap-6 px-4 sm:px-6 lg:px-8 pb-6 border-b border-gray-200">
                {/* LEFT COLUMN */}
                <div className="w-full md:w-2/3 flex flex-col items-center md:items-start relative">
                    <div className="w-full h-48 bg-gray-200 flex items-center justify-center text-gray-500 overflow-hidden rounded-lg shadow-sm">
                        {/* Image Placeholder */}
                    </div>
                    <div className="relative -mt-16 w-full flex flex-col md:flex-row md:items-center md:justify-between px-4">
                        <div className="flex flex-col md:flex-row items-center md:items-start gap-4 mb-4 md:mb-0">
                            <FaUserCircle className="w-32 h-32 rounded-full object-cover p-2 flex-shrink-0 bg-white border-4 border-white shadow-lg text-gray-500" />
                            <div className="flex flex-col text-center md:text-left mt-4 md:mt-0">
                                <h2 className="text-xl font-semibold">{profile.name}</h2>
                                <p className="text-lg">{profile.title}</p>
                            </div>
                        </div>
                        {/* Action Buttons */}
                        <div className="flex justify-center md:justify-end gap-4 w-full md:w-auto">
                           
                            <Button
                                className="bg-purple-900 hover:bg-purple-300 text-white font-semibold py-2 px-6 rounded-lg transition duration-200 shadow-md"
                                onClick={() => initiateChat(parseInt(profile.user_id))} // Call the hook's function
                                    disabled={isLoadingChat} // Disable while the chat is being initiated
                                >
                                {isLoadingChat ? 'Starting Chat...' : 'Message'}  
                            </Button>

                            <Button
                                className="bg-purple-900 hover:bg-purple-300 text-white font-semibold py-2 px-6 rounded-lg transition duration-200 shadow-md"
                                onClick={() => navigate(`/dashboard/send-customer-email/${id}`)}
                            >
                                 Mail
                            </Button>

                            <Button
                                className="bg-primary-brand hover:bg-primary-brand-darker text-white font-semibold py-2 px-6 rounded-lg transition duration-200 shadow-md"
                                onClick={() => navigate(`/dashboard/customer/${id}/edit`)}
                            >
                                Edit Profile
                            </Button>

                            <Button
                                className="bg-gray-700 text-white hover:bg-gray-800 focus:ring-gray-300 dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-gray-800 py-2 px-6"
                                onClick={() => handleDeleteCustomerClick(customerDetails)}
                            >
                                Delete Customer
                            </Button>

                             
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN: Contact & Business Info */}
                <div className="w-full md:w-1/3 p-4 bg-gray-50 rounded-lg shadow-sm text-gray-700 mt-6 md:mt-0">
                    <h3 className="text-xl font-semibold mb-3">Contact &amp; Business Info</h3>
                    <ul className="space-y-2 text-sm">
                        {profile.contactInfo.email && (
                            <li className="flex items-center gap-2">
                                <FaEnvelope className="" />Email: <a href={`mailto:${profile.contactInfo.email}`} className="text-blue-600 hover:underline">{profile.contactInfo.email}</a>
                            </li>
                        )}
                        {profile.contactInfo.phone && (
                            <li className="flex items-center gap-2">
                                <FaPhone className="" />Phone: <a href={`tel:${profile.contactInfo.phone}`} className="text-blue-600 hover:underline">{profile.contactInfo.phone}</a>
                            </li>
                        )}
                        {formattedAddress && (
                            <li className="flex items-center gap-2">
                                <FaMapMarkerAlt className="" />Address: {formattedAddress}
                            </li>
                        )}
                     
                     
                         {profile.contactInfo.createdAt && (
                            <li className="flex items-center gap-2">
                                <FaInfoCircle className="" />Created: {new Date(profile.contactInfo.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                            </li>
                        )}
                        {profile.contactInfo.updatedAt && (
                            <li className="flex items-center gap-2">
                                <FaInfoCircle className="" />Last Updated: {new Date(profile.contactInfo.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            {/* Tabbed Content Sections (Notes and Documents) */}
            <div className="px-4 sm:px-6 lg:px-8 mt-6">
                <Tabs
                    aria-label="Customer Profile Tabs"
                    variant="underline" // You can choose 'default', 'underline', 'pills', etc.
                    onActiveTabChange={(tab) => {
                        // The onActiveTabChange gives the index of the tab, map to names
                        if (tab === 0) setActiveTab('overview');
                        else if (tab === 1) setActiveTab('documents');
                        // Add more conditions for other tabs later if needed
                    }}
                >
                    <TabItem active={activeTab === 'overview'} title="Overview">
                        {/* Notes Section - moved into Overview tab */}
                        <div className="py-6 border-b border-gray-200">
                            <h3 className="text-xl font-semibold mb-3">Notes</h3>
                            <p className=" leading-relaxed">
                                {profile.notes}
                            </p>
                        </div>
                    </TabItem>
                    <TabItem active={activeTab === 'documents'} title="Documents">
                        <div className="py-6 border-b border-gray-200">

                            <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                                <FaFileAlt className="text-blue-600" /> Customer Documents
                            </h3>
                            <div className="flex justify-between items-center mb-4 mt-6">
                                <div>
                                    <Label htmlFor="documentDate" value="Filter Documents by Date" className="mb-2 block" />
                                    <DatePicker
                                        id="documentDate"
                                        selected={selectedDate} // Use 'selected' prop
                                        onChange={(date) => setSelectedDate(date)} // Use 'onChange' prop
                                        dateFormat="yyyy-MM-dd" // Consistent date format for display
                                        className="block w-full p-2.5 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-primary-brand focus:border-primary-brand dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-brand dark:focus:border-primary-brand"
                                        placeholderText="Select a date"
                                        isClearable // Allows clearing the selected date
                                    />
                                    {selectedDate && (
                                        <Button
                                            className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg"
                                            onClick={() => setSelectedDate(null)} // Clear the filter
                                        >
                                            Clear Filter
                                        </Button>
                                    )}
                                </div>

                                <div className='mr-4'>


                                    <Button color="blue" className="bg-primary-brand inline-flex items-right gap-2"
                                        onClick={() => setShowUploadModal(true)}>
                                        <FaPlus />
                                    </Button>
                                </div>
                            </div>

                            {/* Customer Documents Section - moved into Documents tab */}
                            <CustomerDocumentListing
                                customerId={id}
                                documents={customerDocuments}
                                loading={loadingDocuments}
                                error={documentError}
                                onDeleteDocument={handleDeleteDocumentClick}
                                pagination={pagination}
                                onPageChange={onPageChange}
                                onDocumentStatusChange={handleDocumentStatusChange}
                            />
                        </div>
                    </TabItem>

                      <TabItem active={activeTab === 'emails'} title="Emails">
                        <div className="py-6 border-b border-gray-200">

                            <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                                <FaFileAlt className="text-blue-600" /> Emails
                            </h3>
                          

                            {/* Customer Documents Section - moved into Documents tab */}
                            <CustomerEmailLogs customerId={id} customerPage={true} />
                        </div>
                    </TabItem>

                </Tabs>
            </div>


            {/* Confirmation Modal for CUSTOMER deletion */}
            <ConfirmationModal
                show={showDeleteCustomerModal}
                onClose={closeDeleteCustomerModal}
                onConfirm={confirmCustomerDelete}
                loading={deletingCustomer}
                title="Confirm Customer Deletion"
                message={`Are you sure you want to delete customer ${customerToDelete?.full_name || ''}? This action cannot be undone.`}
                confirmText="Yes, delete customer"
                cancelText="No, keep customer"
            />

            {/* NEW: Confirmation Modal for DOCUMENT deletion */}
            <ConfirmationModal
                show={showDeleteDocumentModal}
                onClose={closeDeleteDocumentModal}
                onConfirm={confirmDocumentDelete}
                loading={deletingDocument}
                title="Confirm Document Deletion"
                message={`Are you sure you want to delete the document "${documentToDeleteName}"? This action cannot be undone.`}
                confirmText="Yes, delete document"
                cancelText="No, keep document"
            />


            {showUploadModal && (
                <UploadCustomerDocument
                    isOpen={showUploadModal}
                    customerId={id}
                    onClose={() => setShowUploadModal(false)}
                    onUploadSuccess={() => {
                        fetchCustomerDocuments();
                        setShowUploadModal(false);
                    }}
                />
            )}
        </div>
    );
};

export default ShowCustomerPage;